/* eslint-disable  @typescript-eslint/restrict-template-expressions */
import { stringify } from 'qs';
import { errors as printErrors } from 'pages/VendorsTablePage/components/PrintChecks/messages';
import {
  DEFAULT_HEADER_DATA,
  JAVA_EXCEPTION_ERROR,
  METHOD,
  POLLING_REFRESH_INTERVAL,
  POLLING_STATUS
} from './constants';
import {
  ApiParams,
  AsyncApiParams,
  CheckStatusResponse,
  PollingApiParams
} from './types';

export const apiService = ({
  url,
  method,
  qsObject,
  headers,
  reqObj
}: ApiParams): Promise<Response> => {
  const qs = stringify(qsObject, { addQueryPrefix: true });
  const urlPath = `${url}${qs}`;

  return fetch(urlPath, {
    method,
    headers: {
      ...DEFAULT_HEADER_DATA,
      ...headers
    },
    body: [METHOD.PUT, METHOD.POST, METHOD.PATCH, METHOD.DELETE].includes(
      method
    )
      ? JSON.stringify(reqObj)
      : null
  })
    .then(res => {
      if (!res.ok) {
        return res.text().then(errorMessage => {
          const error = errorMessage.includes(JAVA_EXCEPTION_ERROR)
            ? JAVA_EXCEPTION_ERROR
            : errorMessage;
          throw new Error(error);
        });
      }
      return res;
    })
    .catch(err => {
      throw new Error(err.message);
    });
};

export const getPollingStatus = async ({
  url,
  method,
  qsObject,
  reqObj,
  headers
}: ApiParams): Promise<CheckStatusResponse> => {
  const response = await apiService({
    url,
    method,
    qsObject,
    reqObj,
    headers
  });
  return (await response.json()) as CheckStatusResponse;
};

const pollingApiService = async ({
  url,
  method,
  qsObject,
  uuid,
  headers,
  onUpdate,
  onError
}: PollingApiParams) =>
  new Promise<Response>((resolve, reject) => {
    let timeoutId: NodeJS.Timeout;
    getPollingStatus({ url, method, qsObject, headers })
      .then(pollingStatus => {
        onUpdate?.(pollingStatus);
        if (pollingStatus.status === POLLING_STATUS.COMPLETED) {
          resolve(new Response(pollingStatus.status));
        } else if (
          !pollingStatus.status ||
          pollingStatus.status === POLLING_STATUS.ERROR
        ) {
          onError?.(
            pollingStatus.message || printErrors.printStatusError.defaultMessage
          );
          reject(new Error(pollingStatus.message));
        } else {
          timeoutId = setTimeout(async () => {
            try {
              const result = await pollingApiService({
                url,
                method,
                qsObject,
                uuid,
                headers,
                onUpdate,
                onError
              });
              resolve(result);
            } catch (error) {
              clearTimeout(timeoutId);
              reject(error);
            }
          }, POLLING_REFRESH_INTERVAL);
        }
      })
      .catch(error => {
        onError?.(error.message);
        reject(error);
      });
  });

export const asyncApiService = async ({
  url,
  method,
  qsObject,
  reqObj,
  headers,
  requiredResult = true,
  onUpdate,
  onError
}: AsyncApiParams): Promise<Response> => {
  const response: Response = await apiService({
    url,
    method,
    qsObject,
    reqObj,
    headers
  });
  const text = await response.text();
  const statusPath = `${url}/${text}/status`;
  const status = await pollingApiService({
    url: statusPath,
    method: METHOD.GET,
    qsObject,
    uuid: text,
    headers,
    onUpdate,
    onError
  });
  if (requiredResult) {
    const resultPath = `${url}/${text}/results`;
    return apiService({
      url: resultPath,
      method: METHOD.GET,
      qsObject,
      reqObj,
      headers
    });
  }
  return status;
};
